import { c as _c } from "react/compiler-runtime";
import { MARKUPS_VIEW_MODE_OPTIONS } from "@pages/project-details/project-markups/calendar/project-markups-calendar-utils";
import { areMarkupDueDatesUsedSelector, areMarkupStatusesUsedSelector } from "@store/markups/markups-selector";
import { isFetchingMarkupsIElementsSelector } from "@store/sdb-i-elements/sdb-i-elements-selectors";
import { useAppSelector } from "@store/store-helper";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { DateTime } from "luxon";
import { useSearchParams } from "react-router-dom";

/** Query param that stores whether the annotations are shown in the table, the calendar or the board */
const VIEW_MODE_QUERY_PARAM = "annotationsView";

/** Query param that stores whether the calendar shows a week or a month */
const CALENDAR_VIEW_QUERY_PARAM = "calendarView";

/** Query param that stores a day within the week or month shown in the calendar */
const CALENDAR_DATE_QUERY_PARAM = "calendarDate";

/** Query params of the annotations view, kept when the KPI bar links change the table filters */
export const MARKUPS_VIEW_QUERY_PARAMS = [VIEW_MODE_QUERY_PARAM, CALENDAR_VIEW_QUERY_PARAM, CALENDAR_DATE_QUERY_PARAM];

/** Returns the value if it is one of the view modes, otherwise undefined */
function toViewMode(value, viewModes) {
  return viewModes.find(viewMode => viewMode === value);
}

/**
 * Returns the views that fit how the annotations are used: the calendar needs due dates
 * and the board needs statuses, so users who don't use them are not offered empty views
 */
function useAvailableViewModes() {
  const $ = _c(8);
  const isFetchingMarkups = useAppSelector(isFetchingMarkupsIElementsSelector);
  const areStatusesUsed = useAppSelector(areMarkupStatusesUsedSelector);
  const areDueDatesUsed = useAppSelector(areMarkupDueDatesUsedSelector);
  let t0;
  let t1;
  if ($[0] !== areDueDatesUsed || $[1] !== areStatusesUsed || $[2] !== isFetchingMarkups) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const viewModes = MARKUPS_VIEW_MODE_OPTIONS.map(_temp);
      if (isFetchingMarkups) {
        t1 = viewModes;
        break bb0;
      }
      let t2;
      if ($[5] !== areDueDatesUsed || $[6] !== areStatusesUsed) {
        t2 = viewMode => (viewMode !== "calendar" || areDueDatesUsed) && (viewMode !== "board" || areStatusesUsed);
        $[5] = areDueDatesUsed;
        $[6] = areStatusesUsed;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      t0 = viewModes.filter(t2);
    }
    $[0] = areDueDatesUsed;
    $[1] = areStatusesUsed;
    $[2] = isFetchingMarkups;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  return t0;
}

/** Returns the value if it is a calendar view, otherwise undefined */
function _temp(t0) {
  const {
    value
  } = t0;
  return value;
}
function toCalendarView(value) {
  return value === "week" || value === "month" ? value : undefined;
}
/** Returns the view of the annotations tab, stored in the URL so it survives reloads and can be shared */
export function useMarkupsViewQueryParams() {
  const $ = _c(30);
  const [searchParams, setSearchParams] = useSearchParams();
  const availableViewModes = useAvailableViewModes();
  let t0;
  if ($[0] !== setSearchParams) {
    t0 = function setQueryParam(name, value, isDefault, t1) {
      const shouldReplace = t1 === undefined ? false : t1;
      setSearchParams(params => {
        if (isDefault) {
          params.delete(name);
        } else {
          params.set(name, value);
        }
        return params;
      }, {
        replace: shouldReplace
      });
    };
    $[0] = setSearchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setQueryParam = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== availableViewModes || $[3] !== searchParams || $[4] !== setQueryParam) {
    const calendarDate = DateTime.fromISO(searchParams.get(CALENDAR_DATE_QUERY_PARAM) ?? "");
    if ($[11] !== availableViewModes || $[12] !== searchParams) {
      t1 = toViewMode(searchParams.get(VIEW_MODE_QUERY_PARAM), availableViewModes) ?? toViewMode(LocalStorageUtils.getStringItem("markups-view-mode"), availableViewModes) ?? "table";
      $[11] = availableViewModes;
      $[12] = searchParams;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    t2 = availableViewModes;
    if ($[14] !== setQueryParam) {
      t3 = viewMode => {
        LocalStorageUtils.setStringItem("markups-view-mode", viewMode);
        setQueryParam(VIEW_MODE_QUERY_PARAM, viewMode, false);
      };
      $[14] = setQueryParam;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    if ($[16] !== searchParams) {
      t4 = toCalendarView(searchParams.get(CALENDAR_VIEW_QUERY_PARAM)) ?? toCalendarView(LocalStorageUtils.getStringItem("markups-calendar-view")) ?? "month";
      $[16] = searchParams;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    if ($[18] !== setQueryParam) {
      t5 = calendarView => {
        LocalStorageUtils.setStringItem("markups-calendar-view", calendarView);
        setQueryParam(CALENDAR_VIEW_QUERY_PARAM, calendarView, false);
      };
      $[18] = setQueryParam;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    t6 = calendarDate.isValid ? calendarDate.toJSDate() : new Date();
    $[2] = availableViewModes;
    $[3] = searchParams;
    $[4] = setQueryParam;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[20] !== setQueryParam) {
    t7 = visibleDate => {
      const day = DateTime.fromJSDate(visibleDate);
      setQueryParam(CALENDAR_DATE_QUERY_PARAM, day.toISODate() ?? "", day.hasSame(DateTime.now(), "day"), true);
    };
    $[20] = setQueryParam;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t1 || $[23] !== t2 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t8 = {
      viewMode: t1,
      availableViewModes: t2,
      setViewMode: t3,
      calendarView: t4,
      setCalendarView: t5,
      visibleDate: t6,
      setVisibleDate: t7
    };
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}